import { useAutoAnimate } from '@formkit/auto-animate/react'
import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import cn from 'classnames'
import BaseModal, {
  BaseModalProps,
} from 'ext-ui/components/BaseModal/BaseModal'
import { default as React, ReactElement, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { selectIsToastOpenFromState } from 'src/app/Toast/redux/toast.selectors'
import {
  selectPostingReportTransactionsDownloadFromState,
  selectTopLevelErrorFromState,
} from 'src/component-modal/DownloadTransactionsModal/redux/downloadTransactions.selectors'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import CustomizeCsvDownloadOptions from 'src/components/CustomizeCsvDownloadOptions/CustomizeCsvDownloadOptions'
import ErrorList from 'src/components/ErrorList/ErrorList'
import RadioItems from 'src/components/RadioItems/RadioItems'
import { TransactionsFiltersBody } from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import {
  getTransactionsDownloadFieldsCookie,
  setTransactionsDownloadFieldsCookie,
  TransactionsCSVFields,
} from 'src/cookies'
import { downloadOptions } from 'src/enums'
import { useFlyoutDispatch, useFormFields } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { postReportTransactionsDownload } from 'src/redux/actions/data/reportDownloads.actions'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectReportDownloadLoadingFromState } from 'src/redux/selectors/data/reportDownloads.selectors'
import { css } from 'styled-components'

import style from './DownloadTransactionsModal.style'

export type TransactionDownloadFieldValue = {
  fieldName: string
  value: boolean
}

export type TransactionsDownloadFields = {
  registeredCardLast4: TransactionDownloadFieldValue
  registeredCardName: TransactionDownloadFieldValue
  transactionDate: TransactionDownloadFieldValue
  clearDate: TransactionDownloadFieldValue
  type: TransactionDownloadFieldValue
  status: TransactionDownloadFieldValue
  nameOnCard: TransactionDownloadFieldValue
  vcnRecipientEmail: TransactionDownloadFieldValue
  authorizationMerchantCurrency: TransactionDownloadFieldValue
  authorizationMerchantAmount: TransactionDownloadFieldValue
  authorizationBillingCurrency: TransactionDownloadFieldValue
  authorizationBillingAmount: TransactionDownloadFieldValue
  authorizationExchangeRate: TransactionDownloadFieldValue
  settlementMerchantCurrency: TransactionDownloadFieldValue
  settlementMerchantAmount: TransactionDownloadFieldValue
  settlementBillingCurrency: TransactionDownloadFieldValue
  settlementBillingAmount: TransactionDownloadFieldValue
  settlementExchangeRate: TransactionDownloadFieldValue
  merchantName: TransactionDownloadFieldValue
  merchantAddress: TransactionDownloadFieldValue
  merchantCity: TransactionDownloadFieldValue
  merchantState: TransactionDownloadFieldValue
  merchantCountry: TransactionDownloadFieldValue
  merchantZipPostal: TransactionDownloadFieldValue
  mcc: TransactionDownloadFieldValue
  mccDescription: TransactionDownloadFieldValue
  vcnSender: TransactionDownloadFieldValue
  vcnSenderEmail: TransactionDownloadFieldValue
  vcnNickname: TransactionDownloadFieldValue
  vcnLast4: TransactionDownloadFieldValue
  referenceId: TransactionDownloadFieldValue
  source: TransactionDownloadFieldValue
  external: TransactionDownloadFieldValue
  attachments: TransactionDownloadFieldValue
  transactionAmount: TransactionDownloadFieldValue
  notes: TransactionDownloadFieldValue
  transactionReceipt: TransactionDownloadFieldValue
  receipt: TransactionDownloadFieldValue
  vendor: TransactionDownloadFieldValue
  customer: TransactionDownloadFieldValue
  expenseCategories: TransactionDownloadFieldValue
  numberOfAttachments: TransactionDownloadFieldValue
  typeOfAccount: TransactionDownloadFieldValue
  team: TransactionDownloadFieldValue
  managerReviewStatus: TransactionDownloadFieldValue
  additionalFields: TransactionDownloadFieldValue
}

type DownloadTransactionsModalProps = BaseModalProps & {
  apiQueryParams?: TransactionsFiltersBody
}

export default function DownloadTransactionsModal(
  props: DownloadTransactionsModalProps,
): ReactElement {
  const { apiQueryParams, className, ...rest } = props

  const translate = useTranslate()

  const postingReportTransactionsDownload = useSelector(
    selectPostingReportTransactionsDownloadFromState,
  )
  const reportDownloadLoading = useSelector(
    selectReportDownloadLoadingFromState,
  )
  const {
    enable_custom_fields_expense_fields: customFieldsExpenseFieldsEnabled,
  } = useSelector(selectFeatureFlaggerFromState) ?? {}
  const isToastOpen = useSelector(selectIsToastOpenFromState)
  const topLevelError = useSelector(selectTopLevelErrorFromState)
  const { analytics } = useAnalyticsContext()
  const { QUICKBOOKS_DOWNLOAD: showQuickBooksDownload } =
    useQueryUserFeatures([UserFeature.QUICKBOOKS_DOWNLOAD]).data || {}

  const transactionDownloadFieldsCookie = getTransactionsDownloadFieldsCookie()

  const [connectionOptionsContainerRef] = useAutoAnimate()

  const dispatch = useFlyoutDispatch()

  const [includeAttachments, setIncludeAttachments] = useState(
    transactionDownloadFieldsCookie?.includeAttachments !== undefined
      ? transactionDownloadFieldsCookie.includeAttachments
      : false,
  )
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(
    undefined,
  )
  const [selectedKey, setSelectedKey] = useState<string | undefined>(undefined)
  const [transactionFieldSelectionError, setTransactionFieldSelectionError] =
    useState<boolean>(false)

  const {
    fields: flatFields,
    onFieldChange: onFlatFieldChange,
    onMultiFieldChange: onMultiFlatFieldChange,
  } = useFormFields<TransactionsDownloadFields>(
    {
      registeredCardLast4: {
        fieldName: 'Registered Card Last 4',
        value: transactionDownloadFieldsCookie?.registeredCardLast4 ?? true,
      },
      registeredCardName: {
        fieldName: 'Funding Source',
        value: transactionDownloadFieldsCookie?.registeredCardName ?? true,
      },
      transactionDate: {
        fieldName: 'Transaction Date',
        value: transactionDownloadFieldsCookie?.transactionDate ?? true,
      },
      clearDate: {
        fieldName: 'Clear Date',
        value: transactionDownloadFieldsCookie?.clearDate ?? true,
      },
      type: {
        fieldName: 'Type',
        value: transactionDownloadFieldsCookie?.type ?? true,
      },
      status: {
        fieldName: 'Status',
        value: transactionDownloadFieldsCookie?.status ?? true,
      },
      nameOnCard: {
        fieldName: 'Name on Card',
        value: transactionDownloadFieldsCookie?.nameOnCard ?? true,
      },
      vcnRecipientEmail: {
        fieldName: 'Card User Email',
        value: transactionDownloadFieldsCookie?.vcnRecipientEmail ?? true,
      },
      authorizationMerchantCurrency: {
        fieldName: 'Authorization Merchant Currency',
        value:
          transactionDownloadFieldsCookie?.authorizationMerchantCurrency ??
          true,
      },
      team: {
        fieldName: 'Team',
        value: transactionDownloadFieldsCookie?.team ?? true,
      },
      authorizationMerchantAmount: {
        fieldName: 'Authorization Merchant Amount',
        value:
          transactionDownloadFieldsCookie?.authorizationMerchantAmount ?? true,
      },
      authorizationBillingCurrency: {
        fieldName: 'Authorization Billing Currency',
        value:
          transactionDownloadFieldsCookie?.authorizationBillingCurrency ?? true,
      },
      authorizationBillingAmount: {
        fieldName: 'Authorization Billing Amount',
        value:
          transactionDownloadFieldsCookie?.authorizationBillingAmount ?? true,
      },
      authorizationExchangeRate: {
        fieldName: 'Authorization Exchange Rate',
        value:
          transactionDownloadFieldsCookie?.authorizationExchangeRate ?? true,
      },
      settlementMerchantCurrency: {
        fieldName: 'Settlement Merchant Currency',
        value:
          transactionDownloadFieldsCookie?.settlementMerchantCurrency ?? true,
      },
      settlementMerchantAmount: {
        fieldName: 'Settlement Merchant Amount',
        value:
          transactionDownloadFieldsCookie?.settlementMerchantAmount ?? true,
      },
      settlementBillingCurrency: {
        fieldName: 'Settlement Billing Currency',
        value:
          transactionDownloadFieldsCookie?.settlementBillingCurrency ?? true,
      },
      settlementBillingAmount: {
        fieldName: 'Settlement Billing Amount',
        value: transactionDownloadFieldsCookie?.settlementBillingAmount ?? true,
      },
      settlementExchangeRate: {
        fieldName: 'Settlement Exchange Rate',
        value: transactionDownloadFieldsCookie?.settlementExchangeRate ?? true,
      },
      merchantName: {
        fieldName: 'Merchant Name',
        value: transactionDownloadFieldsCookie?.merchantName ?? true,
      },
      merchantAddress: {
        fieldName: 'Merchant Address',
        value: transactionDownloadFieldsCookie?.merchantAddress ?? true,
      },
      merchantCity: {
        fieldName: 'Merchant City',
        value: transactionDownloadFieldsCookie?.merchantCity ?? true,
      },
      merchantState: {
        fieldName: 'Merchant State',
        value: transactionDownloadFieldsCookie?.merchantState ?? true,
      },
      merchantCountry: {
        fieldName: 'Merchant Country',
        value: transactionDownloadFieldsCookie?.merchantCountry ?? true,
      },
      merchantZipPostal: {
        fieldName: 'Merchant Zip/Postal',
        value: transactionDownloadFieldsCookie?.merchantZipPostal ?? true,
      },
      mcc: {
        fieldName: 'MCC',
        value: transactionDownloadFieldsCookie?.mcc ?? true,
      },
      mccDescription: {
        fieldName: 'MCC Description',
        value: transactionDownloadFieldsCookie?.mccDescription ?? true,
      },
      vcnSender: {
        fieldName: 'VCN Sender',
        value: transactionDownloadFieldsCookie?.vcnSender ?? true,
      },
      vcnSenderEmail: {
        fieldName: 'VCN Sender Email',
        value: transactionDownloadFieldsCookie?.vcnSenderEmail ?? true,
      },
      vcnNickname: {
        fieldName: 'VCN Nickname',
        value: transactionDownloadFieldsCookie?.vcnNickname ?? true,
      },
      vcnLast4: {
        fieldName: 'VCN Last 4',
        value: transactionDownloadFieldsCookie?.vcnLast4 ?? true,
      },
      referenceId: {
        fieldName: 'Reference ID',
        value: transactionDownloadFieldsCookie?.referenceId ?? true,
      },
      source: {
        fieldName: 'Source',
        value: transactionDownloadFieldsCookie?.source ?? true,
      },
      external: {
        fieldName: 'External',
        value: transactionDownloadFieldsCookie?.external ?? true,
      },
      attachments: {
        fieldName: 'Attachments',
        value: transactionDownloadFieldsCookie?.attachments ?? true,
      },
      transactionAmount: {
        fieldName: 'Transaction Amount',
        value: transactionDownloadFieldsCookie?.transactionAmount ?? true,
      },
      notes: {
        fieldName: 'Notes',
        value: transactionDownloadFieldsCookie?.notes ?? true,
      },
      transactionReceipt: {
        fieldName: 'Transaction Receipt',
        value: transactionDownloadFieldsCookie?.transactionReceipt ?? true,
      },
      receipt: {
        fieldName: 'Receipt',
        value: transactionDownloadFieldsCookie?.receipt ?? true,
      },
      vendor: {
        fieldName: 'Vendor',
        value: transactionDownloadFieldsCookie?.vendor ?? true,
      },
      customer: {
        fieldName: 'Customer',
        value: transactionDownloadFieldsCookie?.customer ?? true,
      },
      expenseCategories: {
        fieldName: 'Expense Categories',
        value: transactionDownloadFieldsCookie?.expenseCategories ?? true,
      },
      numberOfAttachments: {
        fieldName: 'Number of Attachments',
        value: transactionDownloadFieldsCookie?.attachmentsCount ?? true,
      },
      typeOfAccount: {
        fieldName: 'Type of Account',
        value: transactionDownloadFieldsCookie?.creditCardType ?? true,
      },
      managerReviewStatus: {
        fieldName: 'Manager Review',
        value: transactionDownloadFieldsCookie?.managerReviewStatus ?? true,
      },
      additionalFields: {
        fieldName: customFieldsExpenseFieldsEnabled
          ? 'Custom Fields'
          : 'Additional Fields',
        value: transactionDownloadFieldsCookie?.additionalFields ?? true,
      },
    },
    () => ({}),
  )

  useEffect((): void => {
    setTransactionFieldSelectionError(
      Object.values(flatFields).every((field) => field.value === false),
    )
  }, [flatFields])

  const onSubmit = (): void => {
    // save fields to cookie
    Object.entries(flatFields).forEach(([key, value]) => {
      setTransactionsDownloadFieldsCookie({
        [key]: value.value,
      })
    })
    setTransactionsDownloadFieldsCookie({
      includeAttachments: includeAttachments,
    })

    selectedKey === downloadOptions.csv &&
      analytics?.track('all transaction export', {
        custom_selection: Object.values(flatFields).some(
          (field) => field.value === false,
        ),
        attachments_included: includeAttachments,
      })

    const transactionCSVFields = Object.entries(flatFields).reduce(
      (acc, [key, value]) => {
        return {
          ...acc,
          [key]: value.value,
        }
      },
      {},
    ) as TransactionsCSVFields

    // initiate download
    const qboReport = selectedKey === downloadOptions.qboForQuickBooks
    dispatch(
      postReportTransactionsDownload(
        apiQueryParams || {},
        qboReport,
        includeAttachments,
        transactionCSVFields,
        () => props.onClose(),
      ),
    )
  }

  const csvOption = (
    <div key={downloadOptions.csv} ref={connectionOptionsContainerRef}>
      <p
        css={css`
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
        `}
      >
        {translate('CSV')}
      </p>
      <p
        css={css`
          ${getFontCss(
            14,
            styleConstants.colors.gray_darkest,
            styleConstants.fontFamilies.regular,
          )}
          margin-top: 8px;
        `}
      >
        {translate(
          'Note: When opening in Excel, be sure to change the file origin to Unicode (UTF-8) to avoid formatting errors',
        )}
      </p>
      {selectedKey === downloadOptions.csv && (
        <CustomizeCsvDownloadOptions
          flatFields={flatFields}
          onFlatFieldChange={onFlatFieldChange}
          onMultiFlatFieldChange={onMultiFlatFieldChange}
          includeAttachments={includeAttachments}
          setIncludeAttachments={setIncludeAttachments}
          transactionFieldSelectionError={transactionFieldSelectionError}
        />
      )}
    </div>
  )

  const radioItems = showQuickBooksDownload
    ? [
        <span key={downloadOptions.qboForQuickBooks}>
          <p
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
            `}
          >
            {translate('Export for QuickBooks')}
          </p>
          <p
            css={css`
              ${getFontCss(
                14,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )}
              margin-top: 8px;
            `}
          >
            {translate(
              'Download your transactions in QBXML to easily import in QuickBooks Desktop or QuickBooks Online',
            )}
          </p>
        </span>,
        csvOption,
      ]
    : [csvOption]

  return (
    <BaseModal
      className={cn('DownloadTransactionsModal', className)}
      css={style}
      aria-label={translate('Download Transactions Modal')}
      {...rest}
    >
      <span
        css={css`
          ${getFontCss(
            32,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
          margin-bottom: 40px;
        `}
      >
        {translate('Download Options')}
      </span>
      <RadioItems
        css={css`
          width: 100%;
        `}
        items={radioItems}
        currentIndex={selectedIndex}
        setIndex={setSelectedIndex}
        setKey={setSelectedKey}
        grayBackground
        staticOnHover
        gap="10px"
        ariaLabels={
          showQuickBooksDownload
            ? [translate('Export for QuickBooks'), translate('CSV')]
            : [translate('CSV')]
        }
      />
      {topLevelError !== undefined && <ErrorList errors={[topLevelError]} />}
      <Button
        css={css`
          margin-top: 40px;
        `}
        type={ButtonTypes.button}
        design={ButtonDesigns.designCta}
        big={true}
        text={
          selectedIndex === undefined
            ? translate('Download')
            : selectedKey === downloadOptions.qboForQuickBooks
              ? translate('Download QBO for QuickBooks')
              : includeAttachments
                ? translate('Download CSV + Attachments')
                : translate('Download CSV')
        }
        testId={'downloadBtn'}
        onClick={onSubmit}
        disabled={
          selectedIndex === undefined ||
          postingReportTransactionsDownload ||
          reportDownloadLoading ||
          isToastOpen ||
          transactionFieldSelectionError
        }
        loading={
          postingReportTransactionsDownload ||
          reportDownloadLoading ||
          isToastOpen
        }
        loadingText={translate('Preparing Files')}
      />
    </BaseModal>
  )
}
